import { LazyQueryHookOptions, QueryHookOptions } from '@apollo/client';

import {
  OpenCommerceUserFilter,
  OpenCommerceUserFragment,
  OpenCommerceUsersQuery,
  OpenCommerceUsersQueryVariables,
  useOpenCommerceUsersQuery,
  useOpenCommerceUsersLazyQuery,
  OpenCommerceUserCurrentQueryVariables,
  OpenCommerceUserCurrentQuery,
  useOpenCommerceUserCurrentQuery,
  useCurrentUserRetailerNetworkUsersQuery,
  CurrentUserRetailerNetworkUsersQuery,
  CurrentUserRetailerNetworkUserFragment,
  RetailerNetworkUsersSort,
  CurrentUserRetailerNetworkUsersQueryVariables,
  UsersEligibleForRetailerConnectFilter,
  useUsersEligibleForRetailerConnectQuery,
  UsersEligibleForRetailerConnectQueryVariables,
  UsersEligibleForRetailerConnectQuery,
  useGetOpenCommerceUserSelectItemsLazyQuery,
} from '@occc/graphql';
import { PaginationInfo, usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';

import { useLazyQuery, useQuery } from '../utils';

type GetOpenCommerceUsersProps = {
  filter: OpenCommerceUserFilter;
  pageSize?: number;
  baseOptions?: QueryHookOptions<OpenCommerceUserFragment[], OpenCommerceUsersQueryVariables>;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
};

const transformDataToOpenCommerceUsersArray = (
  data: OpenCommerceUsersQuery,
): OpenCommerceUserFragment[] => {
  const edges = data.openCommerceUsers?.edges;
  return edges?.map((item) => item.node) || [];
};

export const useGetOpenCommerceUserByUuid = (uuidEquals: string) => {
  const response = useOpenCommerceUsersQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      filter: {
        uuidEquals,
      },
    },
  });

  const transformedData = response.data
    ? transformDataToOpenCommerceUsersArray(response.data)
    : null;

  return {
    ...response,
    data: transformedData ? transformedData[0] : null,
  };
};

export const useGetOpenCommerceUsers = ({
  filter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetOpenCommerceUsersProps) => {
  const response = usePaginatedQuery(
    useOpenCommerceUsersQuery,
    'openCommerceUsers.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter,
      },
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions && baseOptions.onCompleted) {
          baseOptions.onCompleted(transformDataToOpenCommerceUsersArray(data));
        }
      },
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data ? transformDataToOpenCommerceUsersArray(response.data) : response.data,
  };
};

export const useGetOpenCommerceUsersSelectItemsLazy = () => {
  const [executeQuery, response] = useLazyQuery(useGetOpenCommerceUserSelectItemsLazyQuery);

  return {
    executeQuery,
    response: {
      ...response,
      data: response.data?.openCommerceUsers?.edges?.map((e) => e.node),
    },
  };
};

type GetEligibleForRetailerConnectUsersProps = {
  filter: UsersEligibleForRetailerConnectFilter;
  pageSize?: number;
  baseOptions?: QueryHookOptions<
    OpenCommerceUserFragment[],
    UsersEligibleForRetailerConnectQueryVariables
  >;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
};

const transformEligibleForConnectResponse = (
  data: UsersEligibleForRetailerConnectQuery,
): OpenCommerceUserFragment[] => {
  const edges = data.usersEligibleForRetailerConnect?.edges;
  return edges?.map((item) => item.node) || [];
};

export const useGetEligibleForRetailerConnectUsers = ({
  filter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetEligibleForRetailerConnectUsersProps) => {
  const response = usePaginatedQuery(
    useUsersEligibleForRetailerConnectQuery,
    'usersEligibleForRetailerConnect.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter,
      },
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions && baseOptions.onCompleted) {
          baseOptions.onCompleted(transformEligibleForConnectResponse(data));
        }
      },
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data ? transformEligibleForConnectResponse(response.data) : response.data,
  };
};

export const useGetOpenCommerceUsersLazy = (
  baseOptions?: LazyQueryHookOptions<OpenCommerceUsersQuery, OpenCommerceUsersQueryVariables>,
) => {
  const [executeQuery, response] = useLazyQuery<
    OpenCommerceUsersQuery,
    OpenCommerceUsersQueryVariables
  >(useOpenCommerceUsersLazyQuery, baseOptions);

  return {
    executeQuery,
    response: {
      ...response,
      data: response.data ? transformDataToOpenCommerceUsersArray(response.data) : response.data,
    },
  };
};

export const useGetOpenCommerceUserCurrent = (
  baseOptions?: QueryHookOptions<
    OpenCommerceUserCurrentQuery,
    OpenCommerceUserCurrentQueryVariables
  >,
) => useQuery(useOpenCommerceUserCurrentQuery, baseOptions);

type GetCurrentUserRetailerNetworkUsers = {
  retailerNetworkUuid?: string;
  sort?: RetailerNetworkUsersSort;
  pageSize?: number;
  baseOptions?: QueryHookOptions<
    CurrentUserRetailerNetworkUsersQuery,
    CurrentUserRetailerNetworkUsersQueryVariables
  >;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
  skip?: boolean;
};

const transformCurrentUserRetailerNetworkUsersArray = (
  data: CurrentUserRetailerNetworkUsersQuery,
): CurrentUserRetailerNetworkUserFragment[] => {
  const edges = data.currentUserRetailerNetworkUsers?.edges;
  return edges?.map((item) => item.node) || [];
};

export const useGetCurrentUserRetailerNetworkUsers = ({
  retailerNetworkUuid,
  sort,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
  skip,
}: GetCurrentUserRetailerNetworkUsers) => {
  const response = usePaginatedQuery(
    useCurrentUserRetailerNetworkUsersQuery,
    'currentUserRetailerNetworkUsers.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip,
      variables: {
        retailerNetworkUuid,
        sort,
      },
      ...baseOptions,
    },
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data
      ? transformCurrentUserRetailerNetworkUsersArray(response.data)
      : response.data,
  };
};
